/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Film,
  ZoomIn,
  ZoomOut,
  File,
  Loader,
} from "lucide-react";
// Note: react-pdf renders PDFs to a canvas via pdf.js so we can show a real
// first-page image thumbnail and a proper multi-page scrollable preview.
import { Document, Page, pdfjs } from "react-pdf";
import {
  getImageUrl,
  isGoogleDriveUrl,
  getGoogleDriveDirectUrl,
  getGoogleDriveThumbnailUrl,
  extractGoogleDriveFileId,
} from "util/googleDriveHelper";

// Configure the pdf.js worker once. We self-host the worker file (copied into
// /public) so its version matches the pdfjs bundled by react-pdf exactly — no
// CDN dependency, which suits an enterprise deployment.
// Note: keep public/pdf.worker.min.mjs in sync when upgrading react-pdf.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

/** Width (px) the first PDF page is rendered at for thumbnails — kept high
 *  enough to stay crisp when CSS scales it to fill any thumbnail box. */
const PDF_THUMB_RENDER_WIDTH = 360;
/** Cap the rendered page width in the full preview so pages aren't oversized
 *  on very wide screens. */
const PDF_MAX_PAGE_WIDTH = 900;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MediaItem {
  url: string;
  name?: string;
  /** Optional MIME type hint. When omitted, type is inferred from the URL extension. */
  mimeType?: string;
}

type FileKind = "image" | "video" | "pdf" | "other";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function detectKind(item: MediaItem): FileKind {
  const mime = item.mimeType?.toLowerCase() ?? "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime === "application/pdf") return "pdf";
  if (mime) return "other";

  const path = item.url.split("?")[0].toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|svg|bmp|avif)$/.test(path)) return "image";
  if (/\.(mp4|webm|ogg|mov|avi|mkv)$/.test(path)) return "video";
  if (/\.pdf$/.test(path)) return "pdf";
  return "other";
}

function fileExtension(item: MediaItem): string {
  const name = item.name || item.url.split("?")[0];
  const ext = name.split(".").pop()?.toUpperCase();
  return ext || "FILE";
}

/**
 * Google Drive's inline PDF preview embed — renders EVERY page with the native
 * Drive viewer inside an <iframe>.
 *
 * Note: this is why we can't use pdf.js for Drive PDFs — pdf.js must fetch the
 * raw bytes over CORS, but Drive's download endpoint (uc?export=download)
 * responds 403 Forbidden to cross-origin fetches. The /preview embed sidesteps
 * that entirely by rendering server-side.
 */
function getGoogleDrivePreviewUrl(url: string): string {
  const fileId = extractGoogleDriveFileId(url);
  return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : url;
}

/**
 * Renders the first page of a PDF as an image thumbnail.
 * Change: replaces the old static PDF icon so the thumbnail reflects the actual
 * document. Routes by host:
 *   • Google Drive → Drive's thumbnail image API renders the first page and
 *     loads in an <img> without CORS (pdf.js can't fetch Drive bytes — 403).
 *   • S3 / other CORS-enabled hosts → render page 1 via pdf.js.
 * Falls back to the supplied icon while loading or if rendering fails.
 */
function PdfThumbnail({
  item,
  fallback,
}: {
  item: MediaItem;
  fallback: React.ReactNode;
}) {
  const [failed, setFailed] = useState(false);
  // Memoise the pdf.js file source so react-pdf doesn't reload every render.
  const file = useMemo(() => getImageUrl(item.url), [item.url]);

  if (failed) return <>{fallback}</>;

  // Google Drive: the thumbnail service rasterises the first page of a PDF, so
  // a plain <img> works cross-origin. We use the drive.../thumbnail endpoint
  // explicitly (not the lh3 /d/ display URL, which returns raw PDF bytes).
  if (isGoogleDriveUrl(item.url)) {
    return (
      <div className="pdf-thumb bg-white">
        <img
          src={getGoogleDriveThumbnailUrl(item.url, 360)}
          alt={item.name || "PDF preview"}
          className="w-full h-full object-cover"
          style={{ objectPosition: "top center" }}
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }

  // S3 / other CORS-enabled hosts: render page 1 via pdf.js.
  return (
    <div className="pdf-thumb bg-white">
      <Document
        file={file}
        loading={<>{fallback}</>}
        error={<>{fallback}</>}
        onLoadError={() => setFailed(true)}
        onSourceError={() => setFailed(true)}
      >
        <Page
          pageNumber={1}
          width={PDF_THUMB_RENDER_WIDTH}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>
    </div>
  );
}

// ─── Download helpers ─────────────────────────────────────────────────────────

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
  "image/avif": "avif",
  "image/tiff": "tiff",
  "application/pdf": "pdf",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/ogg": "ogg",
  "video/quicktime": "mov",
};

/** Derive a sensible filename with the correct extension for the download. */
export function resolveDownloadName(item: MediaItem): string {
  // Determine extension — prefer mimeType, then URL path
  let ext = "";
  if (item.mimeType) {
    ext = MIME_TO_EXT[item.mimeType.toLowerCase()] ?? item.mimeType.split("/")[1] ?? "";
  }
  if (!ext) {
    const rawExt = item.url.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
    // Only accept extensions that look like real extensions (≤5 chars, alphanumeric)
    if (rawExt && rawExt.length <= 5 && /^[a-z0-9]+$/.test(rawExt)) ext = rawExt;
  }

  const baseName = item.name || "download";
  const existingExt = baseName.split(".").pop()?.toLowerCase() ?? "";
  // If the name already carries a valid matching extension, use it as-is
  if (existingExt && existingExt.length <= 5 && existingExt === ext) return baseName;
  // Strip any existing extension from the name, then append the resolved one
  const stem =
    baseName.includes(".") && existingExt.length <= 5
      ? baseName.slice(0, baseName.lastIndexOf("."))
      : baseName;
  return ext ? `${stem}.${ext}` : baseName;
}

/**
 * Downloads the file cross-origin by fetching it as a Blob.
 *
 * Problem: fetching a Drive display URL (lh3.googleusercontent.com/d/…) for a
 * PDF sometimes returns an HTML auth/consent page (HTTP 200, text/html) because
 * Drive redirects unauthenticated requests.  Saving that HTML blob as ".pdf"
 * produces a "damaged or unrecognised format" error on macOS Preview.
 *
 * Fix:
 *   1. Convert Drive URLs to uc?export=download — that endpoint serves raw
 *      file bytes with Content-Disposition: attachment regardless of auth state.
 *   2. After the fetch, reject text/html blobs (we got a page, not the file).
 *   3. Re-stamp the blob with the known MIME type when S3 returns the generic
 *      application/octet-stream — macOS uses MIME + extension to pick the viewer.
 *   4. Fall back to window.open(downloadUrl): for Drive this triggers an actual
 *      download because uc?export=download carries Content-Disposition:attachment.
 */
export async function triggerDownload(item: MediaItem): Promise<void> {
  const fileName = resolveDownloadName(item);

  // Use the dedicated download endpoint for Drive URLs so we get raw file bytes.
  const downloadUrl = isGoogleDriveUrl(item.url)
    ? getGoogleDriveDirectUrl(item.url)
    : item.url;

  try {
    const response = await fetch(downloadUrl, { mode: "cors" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();

    // If the server returned an HTML page (Drive auth redirect, S3 error XML
    // wrapped in HTML, etc.) the blob is text/html — reject it so we don't
    // save a corrupted file.
    if (blob.type.startsWith("text/html")) {
      throw new Error("Server returned an HTML page instead of file bytes");
    }

    // Re-stamp with the declared MIME type when S3 returns a generic
    // application/octet-stream — the OS uses MIME + extension to open the file.
    const finalBlob =
      item.mimeType && blob.type === "application/octet-stream"
        ? new Blob([blob], { type: item.mimeType })
        : blob;

    const blobUrl = URL.createObjectURL(finalBlob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
  } catch {
    // CORS blocked — use a same-page anchor click instead of window.open(_blank).
    // window.open always opens a new tab first, then closes it after the download
    // starts, causing a disruptive tab flash.
    // A plain anchor click (no target) with Content-Disposition:attachment on the
    // response downloads the file without opening a new tab or navigating away —
    // which is what Drive's uc?export=download endpoint provides.
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

/** Button that downloads the current item using the blob strategy above.
 *  Pass `label` to show text alongside the icon (e.g. "Download File"). */
function DownloadButton({
  item,
  className,
  iconSize = 17,
  label,
}: {
  item: MediaItem;
  className?: string;
  iconSize?: number;
  label?: string;
}) {
  const [busy, setBusy] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await triggerDownload(item);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={className}
      aria-label={`Download ${resolveDownloadName(item)}`}
      title={`Download ${resolveDownloadName(item)}`}
    >
      {busy
        ? <Loader size={iconSize} className="animate-spin" />
        : <Download size={iconSize} />}
      {label && <span>{busy ? "Downloading…" : label}</span>}
    </button>
  );
}

// ─── Thumbnail strip item ─────────────────────────────────────────────────────

function StripThumb({
  item,
  index,
  active,
  onClick,
}: {
  item: MediaItem;
  index: number;
  active: boolean;
  onClick: () => void;
}) {
  const kind = detectKind(item);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all duration-200
        ${active
          ? "border-white scale-110 shadow-lg shadow-black/40"
          : "border-transparent opacity-50 hover:opacity-90 hover:scale-105"
        }`}
      aria-label={`View ${item.name || `item ${index + 1}`}`}
    >
      {kind === "image" ? (
        <img
          src={getImageUrl(item.url)}
          alt={item.name || `Thumbnail ${index + 1}`}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : kind === "video" ? (
        <div className="w-full h-full bg-slate-700 flex items-center justify-center">
          <Film size={18} className="text-white/70" />
        </div>
      ) : kind === "pdf" ? (
        // Change: show the real first page; fall back to the PDF icon on failure.
        <PdfThumbnail
          item={item}
          fallback={
            <div className="w-full h-full bg-red-900/50 flex items-center justify-center">
              <FileText size={18} className="text-red-300" />
            </div>
          }
        />
      ) : (
        <div className="w-full h-full bg-slate-700 flex items-center justify-center">
          <File size={18} className="text-slate-300" />
        </div>
      )}
    </button>
  );
}

// ─── Per-type viewer ──────────────────────────────────────────────────────────

function ImageViewer({ item }: { item: MediaItem }) {
  const [zoomed, setZoomed] = useState(false);

  return (
    <div className="relative flex items-center justify-center w-full h-full">
      <img
        src={getImageUrl(item.url)}
        alt={item.name || "Preview"}
        draggable={false}
        referrerPolicy="no-referrer"
        className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none transition-transform duration-300
          ${zoomed ? "scale-150 cursor-zoom-out" : "cursor-zoom-in"}`}
        onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.opacity = "0.3";
        }}
      />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
        className="absolute top-2 right-2 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all"
        aria-label={zoomed ? "Zoom out" : "Zoom in"}
      >
        {zoomed ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
      </button>
    </div>
  );
}

function VideoViewer({ item }: { item: MediaItem }) {
  return (
    <video
      key={item.url}
      src={item.url}
      controls
      autoPlay={false}
      className="max-w-full max-h-full rounded-lg shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function PdfViewer({ item }: { item: MediaItem }) {
  const isDrive = isGoogleDriveUrl(item.url);
  // Memoise the pdf.js file source so react-pdf doesn't reload on every render.
  const file = useMemo(() => getImageUrl(item.url), [item.url]);
  const [numPages, setNumPages] = useState(0);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(0);

  // Measure the viewport so each page renders crisp at the available width.
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setPageWidth(Math.min(containerRef.current.clientWidth, PDF_MAX_PAGE_WIDTH));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Google Drive: pdf.js can't fetch the bytes (uc?export=download → 403), so
  // use Drive's native /preview embed which renders all pages server-side.
  if (isDrive) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={getGoogleDrivePreviewUrl(item.url)}
          title={item.name || "PDF"}
          allow="autoplay"
          className="w-full h-full rounded-lg border-0 bg-white"
          style={{ minHeight: "72vh", maxWidth: "84vw" }}
        />
      </div>
    );
  }

  // Fallback for non-Drive hosts: if pdf.js can't fetch the bytes (CORS etc.)
  // use the browser's native PDF viewer via <iframe> so the file still opens.
  if (failed) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={`${item.url}#toolbar=1&navpanes=0`}
          title={item.name || "PDF"}
          className="w-full h-full rounded-lg border-0 bg-white"
          style={{ minHeight: "72vh", maxWidth: "84vw" }}
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="pdf-doc w-full h-full overflow-y-auto overflow-x-hidden py-2 flex flex-col items-center scrollbar-hide"
      onClick={(e) => e.stopPropagation()}
    >
      <Document
        file={file}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={() => setFailed(true)}
        onSourceError={() => setFailed(true)}
        loading={
          <div className="flex items-center justify-center h-40 text-white/70">
            <Loader size={22} className="animate-spin" />
          </div>
        }
      >
        {/* Render every page so multi-page PDFs are fully visible & scrollable. */}
        {pageWidth > 0 &&
          Array.from({ length: numPages }, (_, i) => (
            <Page
              key={i}
              pageNumber={i + 1}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
      </Document>
    </div>
  );
}

function OtherViewer({ item }: { item: MediaItem }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-28 h-28 rounded-3xl bg-slate-700/60 border border-slate-600 flex items-center justify-center">
        <File size={52} className="text-slate-300" />
      </div>
      <div className="text-center space-y-1">
        <p className="text-white font-semibold text-base break-all max-w-xs">
          {item.name || "Unknown file"}
        </p>
        <p className="text-slate-400 text-xs uppercase tracking-wider">
          {fileExtension(item)}
        </p>
      </div>
      <DownloadButton
        item={item}
        iconSize={16}
        label="Download File"
        className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors shadow-lg"
      />
    </div>
  );
}

function MediaContent({ item }: { item: MediaItem }) {
  const kind = detectKind(item);
  if (kind === "image") return <ImageViewer item={item} />;
  if (kind === "video") return <VideoViewer item={item} />;
  if (kind === "pdf")   return <PdfViewer item={item} />;
  return <OtherViewer item={item} />;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages open/close state and the active index for MediaPreview.
 *
 * @example
 * const { previewProps, open } = useMediaPreview(files);
 * <button onClick={() => open(0)}>View</button>
 * <MediaPreview {...previewProps} />
 */
export function useMediaPreview(staticItems?: MediaItem[]) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState<MediaItem[]>(staticItems || []);

  const open = useCallback(
    (indexOrItems: number | MediaItem[], index = 0) => {
      if (Array.isArray(indexOrItems)) {
        setItems(indexOrItems);
        setCurrentIndex(index);
      } else {
        if (staticItems) setItems(staticItems);
        setCurrentIndex(indexOrItems);
      }
      setIsOpen(true);
    },
    [staticItems],
  );

  const close = useCallback(() => setIsOpen(false), []);

  return {
    isOpen,
    currentIndex,
    items,
    open,
    close,
    /** Spread directly onto <MediaPreview /> */
    previewProps: {
      items,
      initialIndex: currentIndex,
      isOpen,
      onClose: close,
    },
  };
}

// ─── MediaThumbnail ───────────────────────────────────────────────────────────

export interface MediaThumbnailProps {
  item: MediaItem;
  onClick: () => void;
  className?: string;
  /** Controls the rendered size of the thumbnail box. Defaults to "md". */
  size?: "sm" | "md" | "lg";
}

/**
 * A clickable thumbnail that auto-detects file type.
 * Pair it with MediaPreview to build file galleries.
 *
 * @example
 * <MediaThumbnail item={{ url, name }} onClick={() => open(index)} size="md" />
 */
export const MediaThumbnail: React.FC<MediaThumbnailProps> = ({
  item,
  onClick,
  className = "",
  size = "md",
}) => {
  const kind = detectKind(item);
  const sizeClass = { sm: "h-16 w-16", md: "h-24 w-24", lg: "h-32 w-32" }[size];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700
        hover:border-blue-400 dark:hover:border-blue-500
        shadow-sm hover:shadow-md transition-all duration-200 group cursor-pointer
        ${sizeClass} ${className}`}
      aria-label={`Preview ${item.name || "file"}`}
    >
      {kind === "image" ? (
        <>
          <img
            src={getImageUrl(item.url)}
            alt={item.name || "Image"}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
            <ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </>
      ) : kind === "video" ? (
        <div className="w-full h-full bg-slate-800 dark:bg-slate-900 flex flex-col items-center justify-center gap-1">
          <Film size={22} className="text-slate-300 group-hover:text-blue-400 transition-colors" />
          <span className="text-[10px] text-slate-400">Video</span>
        </div>
      ) : kind === "pdf" ? (
        // Change: render the real first page; fall back to the PDF icon on failure.
        <PdfThumbnail
          item={item}
          fallback={
            <div className="w-full h-full bg-red-50 dark:bg-red-950/40 flex flex-col items-center justify-center gap-1">
              <FileText size={22} className="text-red-400 group-hover:text-red-500 transition-colors" />
              <span className="text-[10px] text-red-400 font-medium">PDF</span>
            </div>
          }
        />
      ) : (
        <div className="w-full h-full bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center gap-1">
          <File size={22} className="text-slate-400 group-hover:text-blue-400 transition-colors" />
          <span className="text-[10px] text-slate-400 truncate px-1">{fileExtension(item)}</span>
        </div>
      )}
    </button>
  );
};

// ─── MediaPreview (Lightbox) ──────────────────────────────────────────────────

interface MediaPreviewProps {
  items: MediaItem[];
  /** Which item to show first. Defaults to 0. */
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Full-screen media lightbox with next/prev navigation, thumbnail strip,
 * keyboard support (←  →  Esc), swipe support, and download.
 *
 * Supports: images (with zoom), videos (with player), PDFs (inline viewer),
 * and generic files (download fallback). Handles Google Drive URLs automatically.
 *
 * @example
 * // Simplest usage with the hook:
 * const { previewProps, open } = useMediaPreview(files);
 * <MediaThumbnail item={files[0]} onClick={() => open(0)} />
 * <MediaPreview {...previewProps} />
 *
 * // Dynamic items (e.g. different lists per row):
 * const { previewProps, open } = useMediaPreview();
 * <button onClick={() => open([{ url, name }], 0)}>View</button>
 * <MediaPreview {...previewProps} />
 */
const MediaPreview: React.FC<MediaPreviewProps> = ({
  items,
  initialIndex = 0,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((i) => (i === 0 ? items.length - 1 : i - 1));
  }, [items.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((i) => (i === items.length - 1 ? 0 : i + 1));
  }, [items.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowLeft")  goToPrev();
      if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, goToPrev, goToNext]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen || items.length === 0) return null;

  const current = items[currentIndex];
  const hasMany = items.length > 1;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) delta < 0 ? goToNext() : goToPrev();
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex flex-col"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-4 py-3 shrink-0 border-b border-white/5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 min-w-0 pr-4">
          {current.name && (
            <p className="text-white/80 text-sm font-medium truncate">{current.name}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasMany && (
            <span className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-xs font-semibold tabular-nums">
              {currentIndex + 1}&nbsp;/&nbsp;{items.length}
            </span>
          )}
          <DownloadButton
            item={current}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all disabled:opacity-50"
          />
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all"
            aria-label="Close"
          >
            <X size={19} />
          </button>
        </div>
      </div>

      {/* ── Main viewer ─────────────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-14 py-4">
        {hasMany && (
          <button
            type="button"
            className="absolute left-3 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 z-10 hover:scale-110"
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            aria-label="Previous"
          >
            <ChevronLeft size={26} strokeWidth={2.5} />
          </button>
        )}

        <div
          className="max-w-[82vw] max-h-[72vh] w-full h-full flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          {/* key forces remount on navigation → resets zoom / video position */}
          <MediaContent key={`${currentIndex}-${current.url}`} item={current} />
        </div>

        {hasMany && (
          <button
            type="button"
            className="absolute right-3 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 z-10 hover:scale-110"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            aria-label="Next"
          >
            <ChevronRight size={26} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* ── Thumbnail strip ──────────────────────────────────────────────────── */}
      {hasMany && (
        <div
          className="shrink-0 pb-4 flex justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-2 px-4 py-2.5 rounded-2xl bg-white/10 backdrop-blur-md max-w-[90vw] overflow-x-auto scrollbar-hide">
            {items.map((item, i) => (
              <StripThumb
                key={i}
                item={item}
                index={i}
                active={i === currentIndex}
                onClick={() => setCurrentIndex(i)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Keyboard hint (shown briefly) ────────────────────────────────────── */}
      {hasMany && (
        <p className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/25 text-xs pointer-events-none select-none hidden sm:block">
          ← → to navigate · Esc to close
        </p>
      )}
    </div>
  );
};

export default MediaPreview;
